import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ASSET_CONDITION_LABELS, isAssetRequestOpen, today } from '@cfl/assets-shared';

import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@/components/cargofl-ui';
import { AccessoryPicker } from '@/components/accessory-picker';
import { PhotoPicker, uploadPendingPhotos } from '@/components/photo-picker';
import {
  ConditionPicker,
  EMPTY_CONDITION,
  type ConditionValue,
} from '@/components/condition-picker';
import { AssetPicker, EmployeePicker } from '@/components/pickers';
import { PageContainer } from '@/components/shell/page-container';
import { ApiError } from '@/lib/api';
import { useAssetRequestQueue } from '@/lib/asset-requests-api';
import { useAssets, type AssetSummary } from '@/lib/assets-api';
import { useAssignableEmployees } from '@/lib/employees-api';
import { useAssignAsset } from '@/lib/operations-api';

/** Damaged is deliberately absent - see assignSchema on the API. */
const HANDOVER_CONDITIONS = ['NEW', 'GOOD', 'FAIR', 'POOR'] as const;

/** Enough of an asset to name it. A register row already holds this much. */
export type AssignTarget = Pick<AssetSummary, 'id' | 'code' | 'name'>;

/** The page's button row. In a dialog this is DialogFooter, which stacks on mobile. */
function PlainFooter({ children }: { children: React.ReactNode }) {
  return <div className="flex gap-2">{children}</div>;
}

/**
 * The handover form itself, shared by the Assign page and the register's row action.
 *
 * ONE copy, deliberately. `fulfilsRequestId` is the only route by which a request is
 * ever marked FULFILLED, and a second hand-copied form is how one entry point quietly
 * loses that field and starts leaving settled requests open behind real handovers.
 *
 * `asset` set means it was opened against one row: the asset is already settled, so
 * there is no picker, the footer is a dialog footer, and a recorded handover closes the
 * dialog rather than resetting for the next one.
 */
function AssignForm({
  asset,
  assets,
  initialEmployeeId = '',
  initialRequestId = '',
  onDone,
  cancelLabel,
}: {
  asset?: AssignTarget;
  /** The choosable AVAILABLE assets. Only read when there is a picker. */
  assets?: AssetSummary[];
  initialEmployeeId?: string;
  initialRequestId?: string;
  /** Cancel, and — in a dialog only — a recorded handover. */
  onDone: () => void;
  cancelLabel: string;
}) {
  const assign = useAssignAsset();
  const { data: employees } = useAssignableEmployees();
  const { data: requests } = useAssetRequestQueue();

  const [assetId, setAssetId] = React.useState(asset?.id ?? '');
  const [employeeId, setEmployeeId] = React.useState(initialEmployeeId);
  const [assignedAt, setAssignedAt] = React.useState(today());
  const [condition, setCondition] = React.useState<(typeof HANDOVER_CONDITIONS)[number]>('GOOD');
  const [remarks, setRemarks] = React.useState('');
  const [accessories, setAccessories] = React.useState<string[]>([]);
  const [conditionRecord, setConditionRecord] = React.useState<ConditionValue>(EMPTY_CONDITION);
  const [photos, setPhotos] = React.useState<File[]>([]);
  const [fulfilsRequestId, setFulfilsRequestId] = React.useState(initialRequestId);

  /**
   * Open requests belonging to the person selected above.
   *
   * Scoped to them deliberately: a request is for a named beneficiary, and issuing this
   * laptop to somebody else does not settle it - the API refuses that outright. Offering
   * the whole queue here would invite exactly that mistake.
   */
  const theirRequests = React.useMemo(
    () =>
      (requests ?? []).filter(
        (request) => isAssetRequestOpen(request.status) && request.forEmployee.id === employeeId,
      ),
    [requests, employeeId],
  );

  // Changing the employee invalidates a request picked for the previous one. Silently
  // keeping it would send a mismatched pair the API then rejects, with the reason
  // pointing at a field the user can no longer see.
  React.useEffect(() => {
    if (fulfilsRequestId && !theirRequests.some((r) => r.id === fulfilsRequestId)) {
      setFulfilsRequestId('');
    }
  }, [theirRequests, fulfilsRequestId]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!assetId || !employeeId) {
      toast.error('Choose both an asset and an employee.');
      return;
    }

    try {
      const result = await assign.mutateAsync({
        assetId,
        employeeId,
        assignedAt,
        condition,
        remarks: remarks.trim() || null,
        accessories,
        fulfilsRequestId: fulfilsRequestId || null,
        faults: conditionRecord.faults,
        conditionNotes: conditionRecord.notes.trim() || null,
        conditionChecked: conditionRecord.checked,
      });
      // Against the handover, not the asset: the point is what it looked like on the
      // day it went to THIS person, which is the same reason faultsAtHandover is frozen.
      if (photos.length > 0) await uploadPendingPhotos('handover', result.id, photos);

      toast.success(
        fulfilsRequestId
          ? `${result.assetCode} issued to ${result.employeeName}, and their request is now closed.`
          : `${result.assetCode} issued to ${result.employeeName}.`,
      );

      // Opened against one row: that asset is out with somebody now, so there is nothing
      // left on this form to submit.
      if (asset) {
        onDone();
        return;
      }

      // Reset the asset but keep the person and date: issuing a laptop, a monitor and
      // a dock to one new joiner is three submissions of nearly the same form.
      setAssetId('');
      setRemarks('');
      // Cleared, unlike the person and the accessories: that request has just been
      // settled, and leaving it selected would send the next handover at a closed
      // request and fail.
      setFulfilsRequestId('');
      // Accessories are NOT reset: kitting out one new joiner is several assets that
      // each come with a charger, and re-ticking it every time is the kind of friction
      // that makes people stop recording it at all.
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'That could not be recorded.');
    }
  };

  const Footer = asset ? DialogFooter : PlainFooter;
  const cancelButton = (
    <Button type="button" variant="secondary" onClick={onDone}>
      {cancelLabel}
    </Button>
  );
  const submitButton = (
    <Button hint={"Hand the asset over — the employee is asked to confirm receipt"} type="submit" disabled={assign.isPending}>
      {assign.isPending ? 'Recording…' : 'Assign asset'}
    </Button>
  );

  return (
    <form onSubmit={submit} className="space-y-4">
      {/* No picker when the asset is already settled — the dialog title names it. */}
      {!asset && (
        <div className="space-y-1.5">
          <Label>Asset</Label>
          <AssetPicker assets={assets} value={assetId} onChange={setAssetId} />
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Employee</Label>
          <EmployeePicker employees={employees} value={employeeId} onChange={setEmployeeId} />
          <p className="text-[11px] text-muted-foreground">
            Anyone who has exited is not listed — they cannot be issued assets.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label>Date of handover</Label>
          <Input
            type="date"
            value={assignedAt}
            max={today()}
            onChange={(e) => setAssignedAt(e.target.value)}
            required
          />
          <p className="text-[11px] text-muted-foreground">
            Cannot be in the future — a handover is recorded when it happens.
          </p>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>Condition at handover</Label>
        <Select
          value={condition}
          onValueChange={(v) => setCondition(v as (typeof HANDOVER_CONDITIONS)[number])}
        >
          <SelectTrigger className="sm:w-[240px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {HANDOVER_CONDITIONS.map((c) => (
              <SelectItem key={c} value={c}>
                {ASSET_CONDITION_LABELS[c]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-[11px] text-muted-foreground">
          Damaged is not offered: issuing something already broken makes the return check
          blame the holder for a fault that predates them. Raise a repair, or note it below.
        </p>
      </div>

      {/*
        Only when the chosen person actually has something outstanding. A field
        that is empty for nine handovers out of ten is a field people stop
        reading, and this one matters: ticking it here is the ONLY way a request
        is ever marked fulfilled, which is what keeps a closed request and a real
        handover from ever disagreeing.
      */}
      {theirRequests.length > 0 && (
        <div className="space-y-1.5 rounded-md border border-primary/30 bg-primary/5 p-3">
          <Label>Settles a request?</Label>
          <Select
            value={fulfilsRequestId || 'none'}
            onValueChange={(v) => setFulfilsRequestId(v === 'none' ? '' : v)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No — just issuing this</SelectItem>
              {theirRequests.map((request) => (
                <SelectItem key={request.id} value={request.id}>
                  {request.category.name}
                  {request.neededBy ? ` · needed by ${request.neededBy}` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] leading-relaxed text-muted-foreground">
            This person has {theirRequests.length} open request
            {theirRequests.length === 1 ? '' : 's'}. Picking one closes it as fulfilled
            against this handover — the only way that status is ever set.
          </p>
        </div>
      )}

      <AccessoryPicker selected={accessories} onChange={setAccessories} />

      {/* Starts empty rather than pre-filled from the asset, and leaving it empty is a
          real answer: the API then copies whatever the asset already had onto this
          handover. Ticking something here means a person just looked at the machine,
          which is better information, so it updates the asset too. */}
      <PhotoPicker
        files={photos}
        onChange={setPhotos}
        label="Photos at handover"
        hint="What it looked like as it went out. Kept against this handover, so a fault reported later can be compared with it rather than argued about."
      />

      <ConditionPicker
        value={conditionRecord}
        onChange={setConditionRecord}
        label="Condition at handover — anything already wrong?"
        hint="Frozen against this handover, so if they report a fault later you can see whether it was already there. Leave it untouched to carry over whatever is already recorded against this asset."
      />

      <div className="space-y-1.5">
        <Label>Remarks</Label>
        <Textarea
          rows={2}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Charger and sleeve included. Small scratch on the lid."
        />
      </div>

      <Footer>
        {asset ? (
          <>
            {cancelButton}
            {submitButton}
          </>
        ) : (
          <>
            {submitButton}
            {cancelButton}
          </>
        )}
      </Footer>
    </form>
  );
}

export function AssignPage() {
  const navigate = useNavigate();
  // Arrived from the request queue: both prefilled, so "issue something against this"
  // is one click from the review dialog rather than a name to remember and retype.
  const [params] = useSearchParams();

  // Only AVAILABLE assets can be issued, so the picker offers only those rather than
  // showing everything and failing on submit.
  const { data: available } = useAssets({ status: 'AVAILABLE', pageSize: 1000 });
  const nothingToIssue = available !== undefined && available.assets.length === 0;

  return (
    <PageContainer>
      <Card className="max-w-3xl">
        <CardHeader>
          <CardTitle className="text-sm">Assign an asset</CardTitle>
          <CardDescription className="text-xs">
            Only assets that are Available appear here. The condition you record is what a
            later return is compared against, so it is worth being honest about a scratch now.
          </CardDescription>
        </CardHeader>

        <CardContent>
          {nothingToIssue ? (
            <Alert>
              <AlertDescription className="text-sm">
                Nothing is available to issue. Take something back from the register — every
                row somebody holds has a Return button — or register more assets.
              </AlertDescription>
            </Alert>
          ) : (
            <AssignForm
              assets={available?.assets}
              initialEmployeeId={params.get('employee') ?? ''}
              initialRequestId={params.get('request') ?? ''}
              onDone={() => navigate('/assets')}
              cancelLabel="Back to the register"
            />
          )}
        </CardContent>
      </Card>
    </PageContainer>
  );
}

/**
 * The same form, opened against one row of the register.
 *
 * Mounted only while it is open, so every open starts clean — there is no reset effect
 * to forget about the next time a field is added. The caller renders it conditionally.
 */
export function AssignDialog({
  asset,
  onOpenChange,
}: {
  asset: AssignTarget;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Assign <span className="at-num">{asset.code}</span>
          </DialogTitle>
          <DialogDescription>
            {asset.name} — the condition you record is what a later return is compared
            against, so it is worth being honest about a scratch now.
          </DialogDescription>
        </DialogHeader>

        <AssignForm asset={asset} onDone={() => onOpenChange(false)} cancelLabel="Cancel" />
      </DialogContent>
    </Dialog>
  );
}
